import { AVAILABILITY, LIST_VISIBILITY, LUX_PLUS_SCHEDULE, ONLINE_PURCHASE } from '~/consts/schedule';
import TENANTS from '~/consts/tenant';

import type { BrandSchedule, CampaignScheduleBrandRow, CampaignScheduleDateEntry } from './types';

export function getBrandDisplayName(brand: string): string {
  return Object.values(TENANTS).find((tenant) => tenant.brand === brand)?.title ?? brand;
}

// Book By Dates / Available Offline are outer, supplementary windows around the main List Visibility (or
// LuxPlus+) schedule, so they're always displayed in this fixed lifecycle order rather than array order.
export const TYPE_DISPLAY_ORDER: Array<BrandSchedule['type']> = [
  AVAILABILITY,
  ONLINE_PURCHASE,
  LIST_VISIBILITY,
  LUX_PLUS_SCHEDULE,
];

// Availability/online_purchase are meaningless without a List Visibility (or LuxPlus+) schedule
// anchoring them — deleting the latter never cascades to the former (see svc-offer's schedule
// validator), so a region with only leftover availability/online_purchase rows isn't actually
// covered by anything customer-facing. Regions/active-status derive from these types only; the
// Dates column still shows all types so the leftover rows remain visible for cleanup.
export const CUSTOMER_FACING_TYPES: Array<BrandSchedule['type']> = [LIST_VISIBILITY, LUX_PLUS_SCHEDULE];

// One representative schedule per type present in the group — same level of simplification the row
// already had (no attempt to reconcile per-region date variance within a single brand's group).
function buildDateEntries(schedules: Array<BrandSchedule>): Array<CampaignScheduleDateEntry> {
  return TYPE_DISPLAY_ORDER.map((type) => schedules.find((schedule) => schedule.type === type))
    .filter((schedule): schedule is BrandSchedule => !!schedule)
    .map((schedule) => ({ type: schedule.type, start: schedule.start, end: schedule.end }));
}

export function buildCampaignScheduleBrandRows(brandSchedules: Array<BrandSchedule>): Array<CampaignScheduleBrandRow> {
  const groups = new Map<string, Array<BrandSchedule>>();

  brandSchedules.forEach((schedule) => {
    const isLuxPlus = schedule.type === LUX_PLUS_SCHEDULE;
    const key = `${schedule.brand}-${isLuxPlus ? LUX_PLUS_SCHEDULE : 'standard'}`;
    const existing = groups.get(key);

    if (existing) {
      existing.push(schedule);
    } else {
      groups.set(key, [schedule]);
    }
  });

  return Array.from(groups.entries()).map(([key, schedules]) => {
    const isLuxPlus = schedules[0].type === LUX_PLUS_SCHEDULE;
    const regions = Array.from(
      new Set(
        schedules
          .filter((schedule) => CUSTOMER_FACING_TYPES.includes(schedule.type))
          .map((schedule) => schedule.region),
      ),
    );

    const brandName = getBrandDisplayName(schedules[0].brand);

    return {
      key,
      brand: isLuxPlus ? `${brandName} - LuxPlus` : brandName,
      isLuxPlus,
      dateEntries: buildDateEntries(schedules),
      regions,
      schedules,
    };
  });
}
